import React, { useEffect, useRef } from 'react'
import CreditCard3D from './CreditCard3D'
import { config } from '../config'

interface HeroProps {
  onContactClick?: () => void
}

const Hero: React.FC<HeroProps> = ({ onContactClick }) => {
  const globeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Initialize Three.js globe animation
    const initGlobe = async () => {
      if (!globeRef.current) return

      try {
        // Dynamically import Three.js to avoid SSR issues
        const THREE = await import('three')
        
        const container = globeRef.current
        const scene = new THREE.Scene()
        const camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000)
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
        
        renderer.setSize(container.clientWidth, container.clientHeight)
        renderer.setPixelRatio(window.devicePixelRatio)
        renderer.setClearColor(0x000000, 0)
        container.appendChild(renderer.domElement)

        // Create earth geometry
        const earthGeometry = new THREE.SphereGeometry(2, 64, 64)
        
        // Create earth material with shader
        const earthMaterial = new THREE.ShaderMaterial({
          uniforms: {
            time: { value: 0 }
          },
          vertexShader: `
            varying vec2 vUv;
            varying vec3 vPosition;
            void main() {
              vUv = uv;
              vPosition = position;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform float time;
            varying vec2 vUv;
            varying vec3 vPosition;
            
            void main() {
              vec3 color1 = vec3(0.0, 0.96, 1.0); // Cyan
              vec3 color2 = vec3(0.48, 0.17, 0.75); // Purple
              
              float noise = sin(vUv.x * 10.0 + time) * sin(vUv.y * 10.0 + time) * 0.1;
              float gradient = (vPosition.y + 2.0) / 4.0 + noise;
              
              vec3 finalColor = mix(color2, color1, gradient);
              gl_FragColor = vec4(finalColor, 0.8);
            }
          `,
          transparent: true,
          wireframe: true
        })

        const earth = new THREE.Mesh(earthGeometry, earthMaterial)
        scene.add(earth)

        // Create network points
        const networkPoints: THREE.Mesh[] = []
        const networkConnections: any[] = []
        const numPoints = 50
        const numConnections = 30

        // Generate random points on sphere surface
        for (let i = 0; i < numPoints; i++) {
          const theta = Math.random() * Math.PI * 2
          const phi = Math.acos(2 * Math.random() - 1)
          const x = 3 * Math.sin(phi) * Math.cos(theta)
          const y = 3 * Math.sin(phi) * Math.sin(theta)
          const z = 3 * Math.cos(phi)

          const pointGeometry = new THREE.SphereGeometry(0.05, 8, 8)
          const pointMaterial = new THREE.MeshBasicMaterial({
            color: 0x00F5FF,
            transparent: true,
            opacity: 0.8
          })
          const point = new THREE.Mesh(pointGeometry, pointMaterial)
          point.position.set(x, y, z)
          networkPoints.push(point)
          scene.add(point)
        }

        // Create connections between points
        for (let i = 0; i < numConnections; i++) {
          const startPoint = networkPoints[Math.floor(Math.random() * networkPoints.length)]
          const endPoint = networkPoints[Math.floor(Math.random() * networkPoints.length)]

          const points = []
          points.push(startPoint.position)
          points.push(endPoint.position)

          const lineGeometry = new THREE.BufferGeometry().setFromPoints(points)
          const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x7B2CBF,
            transparent: true,
            opacity: 0.3
          })

          const line = new THREE.Line(lineGeometry, lineMaterial)
          networkConnections.push({
            line: line,
            startPoint: startPoint,
            endPoint: endPoint,
            progress: Math.random(),
            speed: 0.2 + Math.random() * 0.3
          })
          scene.add(line)
        }

        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4)
        scene.add(ambientLight)

        const pointLight1 = new THREE.PointLight(0x00F5FF, 1)
        pointLight1.position.set(10, 10, 10)
        scene.add(pointLight1)

        const pointLight2 = new THREE.PointLight(0x7B2CBF, 1)
        pointLight2.position.set(-10, -10, -10)
        scene.add(pointLight2)

        // Position camera
        camera.position.z = 8

        // Animation loop
        let time = 0
        const animate = () => {
          requestAnimationFrame(animate)
          time += 0.01

          // Update shader time uniform
          earthMaterial.uniforms.time.value = time

          // Rotate earth
          earth.rotation.y += 0.001

          // Subtle floating motion
          earth.position.y = Math.sin(time) * 0.1

          // Animate network connections
          networkConnections.forEach(conn => {
            conn.progress += conn.speed * 0.01
            if (conn.progress > 1) {
              conn.progress = 0
              // Randomly change end point
              conn.endPoint = networkPoints[Math.floor(Math.random() * networkPoints.length)]
              conn.line.geometry.setFromPoints([conn.startPoint.position, conn.endPoint.position])
            }

            // Update line opacity based on progress
            const opacity = Math.sin(conn.progress * Math.PI) * 0.3
            conn.line.material.opacity = opacity

            // Pulse points
            const scale = 1 + Math.sin(time * 3 + conn.progress * 10) * 0.2
            conn.startPoint.scale.set(scale, scale, scale)
            conn.endPoint.scale.set(scale, scale, scale)
          })

          renderer.render(scene, camera)
        }

        // Handle window resize
        const handleResize = () => {
          if (!container) return
          const width = container.clientWidth
          const height = container.clientHeight

          camera.aspect = width / height
          camera.updateProjectionMatrix()

          renderer.setSize(width, height)
          renderer.setPixelRatio(window.devicePixelRatio)
        }

        window.addEventListener('resize', handleResize)
        animate()

        // Cleanup function
        return () => {
          window.removeEventListener('resize', handleResize)
          if (container && renderer.domElement) {
            container.removeChild(renderer.domElement)
          }
          renderer.dispose()
        }
      } catch (error) {
        console.error('Error initializing globe:', error)
      }
    }

    initGlobe()
  }, [])

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden hero-gradient tech-bg">
      {/* Background Pattern */}
      <div className="absolute inset-0 matrix-bg opacity-30"></div>

      {/* Floating Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-500/10 to-purple-600/10 rounded-full blur-xl floating-element" style={{ animationDelay: '0s' }}></div>
        <div className="absolute bottom-32 right-20 w-24 h-24 bg-gradient-to-r from-purple-500/10 to-blue-600/10 rounded-full blur-xl floating-element" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-full blur-xl floating-element" style={{ animationDelay: '6s' }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto max-sm:py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Improve Your <span className="gradient-text">Payment Systems</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-gray-200">
              With Universal Pays
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Easily integrate wire transfers, cryptocurrency, card payments, and eVoucher solutions into your system for a seamless transaction experience. Expand your payment options and offer your users the flexibility they need with a fast, secure, and efficient setup!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href={config.links.getStarted}
                className="cta-button neon-glow px-8 py-4 rounded-lg text-lg font-semibold text-white hover:shadow-xl transition-all duration-300 text-center inline-block"
              >
                Get Started
              </a>
              <button
                onClick={onContactClick}
                className="glass-card-intense border border-brand-blue text-brand-blue px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-blue hover:text-white transition-all duration-300"
              >
                View Solutions
              </button>
            </div>
          </div>

          {/* Right Content - Credit Cards Display */}
          <div className="flex justify-center lg:justify-end relative">
            {/* Main Credit Cards Image */}
            <div className="relative z-10">
              <img
                src="/assets/andrei-castanha-EPYTXTApTqw-unsplash-removebg-preview.png"
                alt="Credit Cards"
                className="w-full max-w-lg h-auto floating-card"
                style={{ animationDelay: '0s' }}
              />
            </div>

            {/* Additional Floating 3D Cards */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-10 right-10 floating-card opacity-60" style={{ animationDelay: '1s' }}>
                <CreditCard3D type="visa" className="w-24 h-15" />
              </div>
              <div className="absolute bottom-20 left-5 floating-card opacity-50" style={{ animationDelay: '3s' }}>
                <CreditCard3D type="mastercard" className="w-20 h-12" />
              </div>
              <div className="absolute top-1/3 left-10 floating-card opacity-40" style={{ animationDelay: '5s' }}>
                <CreditCard3D type="visa" className="w-18 h-11" />
              </div>
            </div>

            {/* Globe Background (smaller) */}
            <div
              ref={globeRef}
              id="globeContainer"
              className="absolute inset-0 w-full h-full opacity-30 z-0"
            />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <svg className="w-6 h-6 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  )
}

export default Hero
